// "Cosmos": the interactive space background.
//  - The pointer is a gravitational lens: stars behind it bend onto an
//    Einstein ring, and a spacetime grid shows through around it.
//  - Moving the pointer radiates gravitational waves (stronger when faster);
//    they shear stars sideways as they pass. A click is a merger: a chirp.
//  - The three brawlers orbit each other. The one speaking glows and ripples
//    space; a verdict makes them spiral in and merge.
// With prefers-reduced-motion it draws one still frame and stops.
import { lensOffset, waveOffset, waveDone, orbitPositions, orbitSpeed } from "./cosmos-math.js";

const TAU = Math.PI * 2;
const MAX_WAVES = 14;

export function startCosmos(canvas, { seats: SEATS, colors, reducedMotion = false } = {}) {
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) return { speaking() {}, verdict() {}, stop() {} };

  let w = 0;
  let h = 0;
  let dpr = 1;
  let stars = null; // { x, y, z, size, tw, tint } typed arrays
  let nebula = null;
  const pointer = { x: 0, y: 0, active: false, lastX: 0, lastY: 0, travel: 0, lastMove: 0 };
  let lensE = 0; // current Einstein radius, eased toward its target
  const waves = []; // { x, y, t0, amp, wavelength, speed, width, life, color }
  const bodies = { phase: 0, squeeze: 0, merging: 0, glow: [0, 0, 0], speaking: [false, false, false], lastEmit: [0, 0, 0] };
  let frames = 0;
  let raf = 0;
  let last = performance.now();
  let now = 0;

  // ---- setup --------------------------------------------------------------
  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    seedStars();
    paintNebula();
    if (reducedMotion) draw(0);
  }

  // Phones resize on every scroll as the address bar comes and goes: wait for it
  // to settle instead of reseeding the sky each time.
  let resizeTimer = 0;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(resize, 150);
  }

  // A small, deterministic PRNG so the sky doesn't reshuffle on every resize.
  function rng(seed) {
    let s = seed >>> 0;
    return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
  }

  function seedStars() {
    const n = Math.min(1100, Math.round((w * h) / 1500));
    const r = rng(7);
    stars = {
      n,
      x: new Float32Array(n),
      y: new Float32Array(n),
      z: new Float32Array(n), // depth: 0.15 far … 1 near
      size: new Float32Array(n),
      tw: new Float32Array(n), // twinkle phase
      tint: new Uint8Array(n), // 0 white, 1 blue, 2 warm
    };
    for (let i = 0; i < n; i++) {
      stars.x[i] = r() * w;
      stars.y[i] = r() * h;
      const z = Math.pow(r(), 2.2) * 0.85 + 0.15;
      stars.z[i] = z;
      stars.size[i] = 0.5 + z * 1.5 + (r() < 0.015 ? 1.2 : 0);
      stars.tw[i] = r() * TAU;
      const t = r();
      stars.tint[i] = t < 0.7 ? 0 : t < 0.88 ? 1 : 2;
    }
  }

  // Faint nebulae in the three seat colours, painted once per resize.
  function paintNebula() {
    nebula = document.createElement("canvas");
    nebula.width = Math.max(1, Math.round(w / 2));
    nebula.height = Math.max(1, Math.round(h / 2));
    const n = nebula.getContext("2d");
    const g = n.createLinearGradient(0, 0, 0, nebula.height);
    g.addColorStop(0, "#070917");
    g.addColorStop(1, "#020308");
    n.fillStyle = g;
    n.fillRect(0, 0, nebula.width, nebula.height);
    const blobs = [
      [0.12, 0.2, 0.55, colors[SEATS[0]]],
      [0.85, 0.35, 0.5, colors[SEATS[1]]],
      [0.45, 0.95, 0.6, colors[SEATS[2]]],
      [0.6, 0.1, 0.35, "#6d5bd0"],
    ];
    for (const [fx, fy, fr, c] of blobs) {
      const x = fx * nebula.width;
      const y = fy * nebula.height;
      const rad = fr * Math.max(nebula.width, nebula.height);
      const rg = n.createRadialGradient(x, y, 0, x, y, rad);
      rg.addColorStop(0, withAlpha(c, 0.13));
      rg.addColorStop(0.5, withAlpha(c, 0.04));
      rg.addColorStop(1, withAlpha(c, 0));
      n.fillStyle = rg;
      n.fillRect(0, 0, nebula.width, nebula.height);
    }
  }

  // ---- interaction ----------------------------------------------------------
  function emit(x, y, { amp, wavelength = 90, speed = 520, width = 55, life = 2.4, color = "#bcd0ff", delay = 0 }) {
    if (waves.length >= MAX_WAVES) waves.shift();
    waves.push({ x, y, t0: now + delay, amp, wavelength, speed, width, life, color });
  }

  // A merger "chirp": pulses whose wavelength shortens as they come faster.
  function chirp(x, y, color = "#e8eeff", strength = 1) {
    [130, 95, 70, 50, 36].forEach((wl, i) =>
      emit(x, y, { amp: (5 + i * 1.6) * strength, wavelength: wl, width: 40 + wl * 0.3, life: 2.8, color, delay: i * 0.11 }),
    );
  }

  function onMove(e) {
    const x = e.clientX;
    const y = e.clientY;
    if (!pointer.active) {
      pointer.lastX = x;
      pointer.lastY = y;
    }
    const step = Math.hypot(x - pointer.lastX, y - pointer.lastY);
    const dt = Math.max(1, e.timeStamp - pointer.lastMove) / 1000;
    pointer.travel += step;
    // An accelerating mass radiates: emit a pulse for every stretch of travel,
    // stronger the faster the pointer moves.
    if (pointer.travel > 110) {
      const speed = Math.min(step / dt, 3000);
      emit(x, y, { amp: Math.min(1.2 + speed / 420, 6), wavelength: 80 + Math.random() * 40 });
      pointer.travel = 0;
    }
    pointer.x = x;
    pointer.y = y;
    pointer.lastX = x;
    pointer.lastY = y;
    pointer.lastMove = e.timeStamp;
    pointer.active = true;
  }

  function onLeave() {
    pointer.active = false;
  }

  // Leaving the window, or lifting a finger, takes the lens away.
  function onOut(e) {
    if (!e.relatedTarget) onLeave();
  }

  function onUp(e) {
    if (e.pointerType === "touch") onLeave();
  }

  function onDown(e) {
    if (e.target.closest?.("button, a, input, select, textarea, label, summary, .box, .verdict")) return;
    chirp(e.clientX, e.clientY);
  }

  // ---- frame ----------------------------------------------------------------
  function displace(x, y, lensScale) {
    let ox = 0;
    let oy = 0;
    if (lensE > 0.5 && lensScale > 0) {
      const [lx, ly] = lensOffset(x - pointer.x, y - pointer.y, lensE * lensScale);
      ox += lx;
      oy += ly;
    }
    for (const wv of waves) {
      const age = now - wv.t0;
      if (age <= 0) continue;
      const [wx, wy] = waveOffset(x - wv.x, y - wv.y, age, wv);
      ox += wx;
      oy += wy;
    }
    return [ox, oy];
  }

  function draw(dt) {
    ctx.drawImage(nebula, 0, 0, w, h);

    // Lens strength eases in while the pointer is over the page.
    const target = pointer.active && !reducedMotion ? 34 : 0;
    lensE += (target - lensE) * Math.min(1, dt * 4);

    // Pointer parallax: near stars shift a little opposite to the pointer.
    const px = pointer.active ? (pointer.x - w / 2) / w : 0;
    const py = pointer.active ? (pointer.y - h / 2) / h : 0;
    const s = stars;
    const tints = ["#ffffff", "#bcd3ff", "#ffd9b0"];
    for (let t = 0; t < 3; t++) {
      ctx.fillStyle = tints[t];
      for (let i = 0; i < s.n; i++) {
        if (s.tint[i] !== t) continue;
        const z = s.z[i];
        // Slow drift, faster for nearer stars; wraps around the screen.
        s.x[i] -= dt * (2 + z * 9);
        if (s.x[i] < -4) s.x[i] += w + 8;
        let x = s.x[i] - px * z * 18;
        let y = s.y[i] - py * z * 18;
        const [ox, oy] = displace(x, y, 1.15 - z * 0.5); // far stars are lensed more
        x += ox;
        y += oy;
        const twinkle = reducedMotion ? 0.85 : 0.65 + 0.35 * Math.sin(now * (1 + z * 2) + s.tw[i]);
        ctx.globalAlpha = Math.min(1, (0.25 + z * 0.75) * twinkle);
        const size = s.size[i];
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
      }
    }
    ctx.globalAlpha = 1;

    drawGrid();
    drawWaves();
    drawLensRing();
    drawBodies(dt);

    for (let i = waves.length - 1; i >= 0; i--) {
      if (waveDone(now - waves[i].t0, waves[i], Math.hypot(w, h))) waves.splice(i, 1);
    }
  }

  // The fabric of spacetime, visible only near the pointer, bent by the lens
  // and waves. Segments are bucketed by distance so the grid fades out.
  function drawGrid() {
    if (!pointer.active || lensE < 1) return;
    const R = 230;
    const step = 34;
    const x0 = Math.floor((pointer.x - R) / step) * step;
    const y0 = Math.floor((pointer.y - R) / step) * step;
    const bands = [new Path2D(), new Path2D(), new Path2D()];
    const bandOf = (d) => (d < R * 0.45 ? 0 : d < R * 0.75 ? 1 : 2);
    for (let pass = 0; pass < 2; pass++) {
      for (let k = 0; k <= (2 * R) / step + 1; k++) {
        let prev = null;
        for (let j = 0; j <= (2 * R) / 8; j++) {
          const gx = pass === 0 ? x0 + k * step : pointer.x - R + j * 8;
          const gy = pass === 0 ? pointer.y - R + j * 8 : y0 + k * step;
          const d = Math.hypot(gx - pointer.x, gy - pointer.y);
          if (d > R) {
            prev = null;
            continue;
          }
          const [ox, oy] = displace(gx, gy, 0.9);
          const p = [gx + ox, gy + oy];
          if (prev) {
            const band = bands[bandOf(d)];
            band.moveTo(prev[0], prev[1]);
            band.lineTo(p[0], p[1]);
          }
          prev = p;
        }
      }
    }
    ctx.lineWidth = 1;
    const strength = 0.17 * (lensE / 34);
    [1, 0.5, 0.18].forEach((f, i) => {
      ctx.strokeStyle = `rgba(150, 175, 255, ${(strength * f).toFixed(3)})`;
      ctx.stroke(bands[i]);
    });
  }

  function drawWaves() {
    ctx.lineWidth = 1.2;
    for (const wv of waves) {
      const age = now - wv.t0;
      if (age <= 0) continue;
      const r = wv.speed * age;
      const a = 0.13 * Math.exp(-age / (wv.life * 0.6)) * Math.min(1, wv.amp / 4);
      if (a < 0.005) continue;
      ctx.strokeStyle = withAlpha(wv.color, a);
      ctx.beginPath();
      ctx.arc(wv.x, wv.y, r, 0, TAU);
      ctx.stroke();
    }
  }

  function drawLensRing() {
    if (lensE < 1) return;
    const g = ctx.createRadialGradient(pointer.x, pointer.y, lensE * 0.6, pointer.x, pointer.y, lensE * 1.4);
    g.addColorStop(0, "rgba(0, 0, 0, 0)");
    g.addColorStop(0.45, `rgba(190, 210, 255, ${0.1 * (lensE / 34)})`);
    g.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(pointer.x, pointer.y, lensE * 1.4, 0, TAU);
    ctx.fill();
  }

  // The three brawlers, orbiting high on the right behind the header.
  function drawBodies(dt) {
    const cx = w * (w < 700 ? 0.78 : 0.84);
    const cy = Math.min(110, h * 0.12);
    const radius = Math.min(70, w * 0.09);
    if (bodies.merging > 0) {
      bodies.merging += dt;
      const t = bodies.merging;
      if (t < 2.6) bodies.squeeze = Math.min(0.97, Math.pow(t / 2.6, 2.4));
      else if (!bodies.merged) {
        bodies.merged = true;
        chirp(cx, cy, "#fff4e0", 1.4);
      } else if (t < 7) bodies.squeeze = 0.97 * (1 - (t - 2.6) / 4.4);
      else {
        bodies.merging = 0;
        bodies.merged = false;
        bodies.squeeze = 0;
      }
    }
    bodies.phase += dt * orbitSpeed(48, bodies.squeeze);
    const pos = orbitPositions(bodies.phase, { cx, cy, radius, squeeze: bodies.squeeze });
    pos.forEach(([x, y], i) => {
      const seat = SEATS[i];
      const on = bodies.speaking[i];
      bodies.glow[i] += ((on ? 1 : 0) - bodies.glow[i]) * Math.min(1, dt * 3);
      if (on && now - bodies.lastEmit[i] > 0.9) {
        bodies.lastEmit[i] = now;
        emit(x, y, { amp: 2.2, wavelength: 70, speed: 380, width: 40, life: 2, color: colors[seat] });
      }
      const glow = bodies.glow[i];
      const r = 2.6 + glow * 2.2;
      const halo = ctx.createRadialGradient(x, y, 0, x, y, 16 + glow * 26);
      halo.addColorStop(0, withAlpha(colors[seat], 0.55 + glow * 0.35));
      halo.addColorStop(1, withAlpha(colors[seat], 0));
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(x, y, 16 + glow * 26, 0, TAU);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(x, y, r, 0, TAU);
      ctx.fill();
    });
  }

  function loop(t) {
    const dt = Math.min(0.05, (t - last) / 1000);
    last = t;
    now += dt;
    draw(dt);
    frames++;
    if (frames % 15 === 0) {
      canvas.dataset.frames = String(frames);
      canvas.dataset.waves = String(waves.length);
      canvas.dataset.lens = lensE.toFixed(1);
      canvas.dataset.speaking = SEATS.filter((_, i) => bodies.speaking[i]).join(" ");
      canvas.dataset.merging = bodies.merging > 0 ? "1" : "0";
    }
    raf = requestAnimationFrame(loop);
  }

  function onVisibility() {
    cancelAnimationFrame(raf);
    if (!document.hidden && !reducedMotion) {
      last = performance.now();
      raf = requestAnimationFrame(loop);
    }
  }

  window.addEventListener("resize", onResize);
  resize();
  if (!reducedMotion) {
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("mouseout", onOut);
    window.addEventListener("blur", onLeave);
    document.addEventListener("visibilitychange", onVisibility);
    raf = requestAnimationFrame(loop);
  }
  canvas.dataset.mode = reducedMotion ? "still" : "live";

  return {
    // The seat that has the floor glows and ripples space; false when its turn ends.
    speaking(seat, on) {
      const i = SEATS.indexOf(seat);
      if (i >= 0) bodies.speaking[i] = on;
    },
    // The brawl reached a verdict: the three spiral together and merge.
    verdict() {
      if (!reducedMotion && !bodies.merging) bodies.merging = 0.0001;
    },
    stop() {
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("mouseout", onOut);
      window.removeEventListener("blur", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    },
  };
}

// "#rrggbb" + alpha -> "rgba(…)".
export function withAlpha(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${Math.max(0, Math.min(1, a)).toFixed(3)})`;
}
